// gloss:file

'use client';

import { Button } from '@zealot/ui/components/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@zealot/ui/components/tooltip';
import { cn } from '@zealot/ui/lib/utils';
import type { ComponentProps } from 'react';

// gloss
type HintedButtonProps = Omit<ComponentProps<typeof Button>, 'disabled'> & {
  reason?: string;
  hint?: string;
};

// gloss
// why: `reason` blocks with `aria-disabled` and a click guard, never native `disabled` — a natively
// why: disabled button dispatches no pointer events in Chrome, so the tooltip saying why is the one
// why: thing the user could never reach.
// why: the trigger is mounted unconditionally and only the CONTENT is gated — swapping the wrapper
// why: in and out as the reason clears remounts the button, and a click already in flight lands on
// why: a detached node.
const HintedButton = ({ reason, hint, onClick, className, children, ...props }: HintedButtonProps) => {
  const isBlocked = reason !== undefined;
  const tooltip = reason ?? hint;

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            type="button"
            aria-disabled={isBlocked}
            onClick={isBlocked ? undefined : onClick}
            className={cn(isBlocked && 'cursor-not-allowed opacity-50', className)}
            {...props}
          >
            {children}
          </Button>
        }
      />
      {tooltip && <TooltipContent>{tooltip}</TooltipContent>}
    </Tooltip>
  );
};

export type { HintedButtonProps };
export { HintedButton };
